import { Stack, IconButton, Paper, Box } from '@mui/material';
import { EditRounded } from '@mui/icons-material';
import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Device } from '@/core/entities';
import { PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import { PaginationMeta } from '@/shared/types/pagination';
import DeviceNameUpdateForm from '@/features/devices/DeviceNameUpdateForm';
import { useSiteDevices } from '@/queries/sites';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting } from 'devextreme-react/data-grid';
import dayjs from 'dayjs';
import { DATE_FORMAT, TIME_FORMAT } from '@/shared/constants/date';

interface SiteDevicesTableProps {
	siteId?: string;
	customerName?: string;
}

const SiteDevicesTable: FunctionComponent<SiteDevicesTableProps> = ({
	siteId,
	customerName,
}): ReactElement => {
	const queryClient = useQueryClient();

	const [devices, setDevices] = useState<Device[]>([]);
	const [, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);

	const {
		queryKey: devicesQueryKey,
		query: { isFetching, data: devicePages },
	} = useSiteDevices(siteId, { limit: 99999 });

	useEffect(() => {
		const devicePage = devicePages?.pages[0];
		if (devicePage) {
			const { page: currentPage, totalItems, totalPages, itemsPerPage: pageSize } = devicePage;
			setDevices(devicePage.data);
			setPaginationMeta({ totalItems, totalPages, currentPage, pageSize });
		}
	}, [devicePages]);

	// Device form
	const [deviceForm, setDeviceForm] = useState<{
		open: boolean;
		model?: Device;
	}>({
		open: false,
		model: undefined,
	});

	const refetch = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(devicesQueryKey);
	};

	const handleEdit = (device: Device) => {
		setDeviceForm({ model: device, open: true });
	};

	const renderActionsCell = (cellData: { data: Device }) => {
		const device = cellData.data;
		return (
			<Stack direction="row" spacing={1}>
				<IconButton
					size="small"
					color="primary"
					onClick={(e) => {
						e.stopPropagation();
						handleEdit(device);
					}}
				>
					<EditRounded fontSize="small" />
				</IconButton>
			</Stack>
		);
	};

	const renderLastSyncDateCell = (cellData: { data: Device }) => {
		const { lastSyncDate } = cellData.data;
		const date = lastSyncDate && dayjs(lastSyncDate);
		if (!date) return null;
		return (
			<>
				{date.format(DATE_FORMAT)}
				<br />
				{date.format(TIME_FORMAT)}
			</>
		);
	};

	return (
		<Stack direction="column" spacing={2.5}>
			{isFetching ? (
				<Backdrop />
			) : (
				<Box component={Paper} p={3}>
					<DataGrid
						dataSource={devices}
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						allowColumnResizing={true}
						allowColumnReordering={true}
					>
						<Sorting mode="multiple" />
						<FilterRow visible={true} />
						<HeaderFilter visible={true} />
						<SearchPanel visible={true} highlightCaseSensitive={true} />
						<Paging defaultPageSize={50} enabled={true} />

						<Column dataField="name" caption="Device Name" />
						<Column dataField="serialNumber" caption="Serial No." />
						<Column
							dataField="lastSyncDate"
							caption="Last Sync Date"
							alignment="right"
							cellRender={renderLastSyncDateCell}
						/>
						<Column
							caption="Actions"
							width={80}
							allowFiltering={false}
							allowSorting={false}
							allowReordering={false}
							cellRender={renderActionsCell}
						/>
					</DataGrid>
				</Box>
			)}
			<DeviceNameUpdateForm
				open={deviceForm.open}
				onClose={() => setDeviceForm({ ...deviceForm, open: false })}
				device={deviceForm.model!}
				onSubmitted={refetch}
				customerName={customerName}
			/>
		</Stack>
	);
};

export default SiteDevicesTable;
